import { reconcileLightChannels, rejectLightChannels } from '../Utils/light_channels_transaction';
import { normalizeScheduleMetadata } from '../Utils/scheduling_utils';
import { reconcileOutputMonitor } from '../Utils/output_monitor';
import {
  reconcileScheduleSubmission,
  rejectScheduleSubmission,
  scheduleSubmissionMessage
} from '../Utils/scheduling_transaction';
import { convert_settings } from '../Utils/utils.js';
import { applyControllerApiVersion } from '../Utils/controller_metadata.js';
import { RemoveMessageOfController, UpdateHistory, TransformHistoryValue } from '../Utils/utils.js';
import { HISTORY_VALUES, resolutions } from '../Constants/constants';
import { initialHistory } from '../Constants/InitialSettings.js';
import { hasFullSettingsPayload } from '../Utils/update_values_utils.js';
import { infoGraphHasRenderableSamples } from '../Utils/info_graph_utils.js';
import { getHistoryTranslationKey } from '../Utils/history_display_utils.js';
import { recordControllerValuesReceived } from '../Utils/controller_connection_utils.js';
import { supportsCharacteristicInfoGraphs } from '../Utils/info_graph_utils.js';
import { infoGraphKeys } from '../Utils/info_graph_subscription.js';
import {
  reconcilePresentCategories,
  reconcileSlavesState,
  reconcileState,
  reconcileStatePreservingFields
} from '../Utils/reconcile_state.js';
import { websocketDebug } from '../Utils/websocket_logging.js';
import { readStoredSettingsDraft } from '../Utils/settings_draft';
import {
  alignDownToHistoryResolution,
  completeHistoryRequest,
  markHistoryChunkReceived,
  shouldAcceptHistoryChunk,
  supportsStableHistoryTransferId
} from '../Utils/history_utils.js';

import _ from 'lodash';
import { ref } from 'valtio';

function logFirmwareDiagnostics(controllerId, messageParts, text) {
  if (messageParts[0] !== 'current_firware_version_wifi') return false;
  console.info('[firmware-diagnostics]', controllerId, text);
  return true;
}

export default function controllerOnMessage(wsManager, resp) {
  const controller_id = resp !== undefined ? resp.internal_id : undefined;
  const cur_controller = wsManager.store.getControllerById(controller_id);
  const controller_name = cur_controller !== undefined ? cur_controller.name : controller_id;

  const messages = wsManager.store.messages;
  const data = resp['data'] ?? {};

  const hooks = wsManager.store.hooks;

  // Parse message and get command.
  if (!('command' in resp)) console.log('[cont-ws] invalid command!');
  let command = resp['command'];
  if (command !== 'update_values') {
    websocketDebug('[cont-ws] received', { command, controllerId: controller_id });
  }

  if (command === 'controller_deleted') {
    try {
      sessionStorage.setItem('controllers', JSON.stringify(wsManager.store.controllers));
    } catch (error) {
      console.error('[cont-ws] Could not save controllers to sessionStorage:', error);
    }
    hooks.enqueueSnackbar(controller_name + ' - deleted', { variant: 'info' });
    window.location.reload();
  } else if (command === 'controller_deleted_failed') {
    hooks.enqueueSnackbar(controller_name + ' - failed to delete', { variant: 'error' });
  }

  // cmd: message:
  if (command === 'message') {
    let messageParts = data.message_parts;
    if (rejectLightChannels(cur_controller, data)) {
      // SAM sends the ordinary category/count result after this lifecycle event.
      if (data.message_parts[3] === 'controller_rejected') return;
      messageParts = ['lights', ': ', 'gc_ms_settings_rejected', ' - ',
        `settings_upload_${data.message_parts[3]}`];
    }
    if (rejectScheduleSubmission(cur_controller, data)) {
      // SAM sends the ordinary category/count result after this lifecycle event.
      if (data.message_parts[3] === 'controller_rejected') return;
      messageParts = ['scheduling', ': ', 'gc_ms_settings_rejected', ' - ',
        scheduleSubmissionMessage({ status: 'rejected', reason: data.message_parts[3] })];
    }
    // Create message, by translating simple string or several strings in array.
    let text = '';
    for (const part of messageParts) {
      text += hooks.t(getHistoryTranslationKey(cur_controller, part));
    }
    // Version reports are support diagnostics, including before settings arrive.
    if (logFirmwareDiagnostics(controller_id, data.message_parts, text)) return;
    // Get other data.
    const type = data['type'] === 'information' ? 'info' : data['type'];
    // Add snackbar
    hooks.enqueueSnackbar(controller_name + ' - ' + text, { variant: type });
    // If not blocked, add to log.
    if (!data['dont_log']) {
      const timestamp = data['timestamp'];
      const newMessage = {
        timestamp: timestamp,
        id: timestamp + text,
        type: type,
        message: text,
        controller: controller_name,
        controller_id
      };

      messages.messages.push(newMessage);
      messages.unreadMessages = true;
    }
  }

  else if (command === 'send_database_logging' && cur_controller) {
    // History pages replace complete responses; their receipts are never edited.
    // Avoid making every historical value part of the live controller proxy tree.
    cur_controller.database_log_response = ref(data);
  }

  //cmd: send_all_messages
  else if (command === 'send_all_messages') {
    // Remove all messages from this controller
    let currentMessages = RemoveMessageOfController(
      wsManager.store.messages.messages,
      controller_id
    );
    const otherControllerMessageCount = currentMessages.length;
    // Add new messages for this controller.
    const newMessages = data['all_messages'];
    for (var i = 0; i < newMessages.length; i++) {
      const message = newMessages[i];
      let text = '';
      for (const part of message['message_parts']) {
        text += hooks.t(getHistoryTranslationKey(cur_controller, part));
      }
      if (logFirmwareDiagnostics(controller_id, message.message_parts, text)) continue;
      const type = message['type'] === 'information' ? 'info' : message['type'];
      const timestamp = message['timestamp'];
      currentMessages.push({
        timestamp: timestamp,
        id: timestamp + text,
        type: type,
        message: text,
        controller: controller_name,
        controller_id
      });
    }

    messages.messages = currentMessages;
    messages.unreadMessages = currentMessages.length > 0 &&
      (messages.unreadMessages || currentMessages.length > otherControllerMessageCount);
  }

  //cmd: resp_action
  else if (command === 'resp_action') {
    const new_info = resp['data']['info'];
    let message =
      'For ' +
      new_info['id'] +
      ('value' in new_info ? ' with value ' + new_info['value'] : '') +
      ': ';
    let type = '';
    if (resp.data['success'] === true) {
      message += 'success';
      type = 'success';
    } else {
      message += 'failed';
      type = 'error';
    }

    hooks.enqueueSnackbar(message, { variant: type });
  }

  // cmd: update_values:
  else if (command === 'update_values') {
    recordControllerValuesReceived(cur_controller.connection);
    reconcileLightChannels(cur_controller, data);

    if ('zones' in data) {
      let found = [];

      for (let zone of data.zones) {
        if (cur_controller.zones[zone.id] == null) {
          const history = _.cloneDeep(initialHistory);
          history.hidden = _.cloneDeep(cur_controller.history.data.hidden);
          const identity = { id: zone.id, zone_id: zone.id, controller_id };
          cur_controller.zones[zone.id] = {
            ...identity,
            settings: {},
            temp_settings: readStoredSettingsDraft(identity),
            infos: {},
            history: { data: history },
            skipped_values: []
          };
        }

        if (cur_controller.zones[zone.id].name !== zone.name) {
          cur_controller.zones[zone.id].name = zone.name;
        }

        update_values(cur_controller.zones[zone.id], zone.settings, cur_controller.type);
        found.push(zone.id);
      }

      // delete obsolete zones
      for (let zone of Object.values(cur_controller.zones)) {
        if (found.indexOf(zone.id) === -1) {
          delete cur_controller.zones[zone.id];
        }
      }

      if ('multi_io_counters' in data) {
        cur_controller.multi_io_counters = reconcileState(
          cur_controller.multi_io_counters ?? {},
          data.multi_io_counters
        );
      }

      if ('slaves' in data) {
        cur_controller.slaves = reconcileSlavesState(cur_controller.slaves, data.slaves);
      }

      if ('overwr_io' in data) {
        // Maintain this logic. Consumers check presence only (`'overwr_io' in
        // controller`), so never store an empty value.
        if (data.overwr_io != null) cur_controller.overwr_io = data.overwr_io;
        else delete cur_controller.overwr_io;
      }

      if ('user_func_sets' in data) {
        cur_controller.settings.user_func_sets = reconcileState(
          cur_controller.settings?.user_func_sets,
          data.user_func_sets
        );
      }

      // BAIA GLOBAL categories are siblings of zones, never zone-zero settings.
      for (const category of ['nutrition', 'irrigation_tank_info']) {
        if (category in data) {
          cur_controller.settings[category] = reconcileState(
            cur_controller.settings[category],
            data[category]
          );
        }
      }

      if ('ext_readings' in data) {
        cur_controller.ext_readings = reconcileState(
          cur_controller.ext_readings,
          data.ext_readings
        );
      }

      if ('ahu_info' in data) {
        cur_controller.ahu_info = reconcileState(cur_controller.ahu_info, data.ahu_info);
      }

      if ('ahu_unit' in data) {
        cur_controller.ahu_unit = reconcileState(cur_controller.ahu_unit, data.ahu_unit);
      }

      if ('system' in data) {
        cur_controller.settings.system = reconcileState(
          cur_controller.settings?.system ?? {},
          data.system
        );
      }

      if ('alarms' in data) {
        cur_controller.settings.alarms = reconcileState(
          cur_controller.settings?.alarms ?? {},
          data.alarms
        );
      }

      if ('hvac' in data) {
        cur_controller.settings.hvac = reconcileState(cur_controller.settings.hvac, data.hvac);
      }
    } else {
      update_values(cur_controller, data);
    }

    // Check this response, not merged state: independent partial updates must
    // not release forms before every permitted zone/global setting is present.
    if (hasFullSettingsPayload(data, cur_controller)) {
      cur_controller.connection.request_values_full = true;
    }

    const liveApiVersion = data.system?.api_version;
    if (String(liveApiVersion ?? '').trim() !== '') {
      wsManager.store.recordControllerApiVersion?.(controller_id, liveApiVersion);
      applyControllerApiVersion(cur_controller, liveApiVersion);
    }
    // Apply this response's API version before accepting or clearing monitoring.
    if ('zones' in data) reconcileOutputMonitor(cur_controller, data);

    // Partial live updates can arrive before the requested full snapshot. Keep
    // forms unmounted until the full-settings check above succeeds for all zones.
    if (cur_controller.connection.request_values_full) {
      cur_controller.connection.awaiting_values = false;
    }
  }
  // cmd: send_info_graphs
  else if (command === 'send_info_graphs') {
    console.debug('[info-graphs][recv]', {
      controllerId: controller_id,
      zoned: 'zones' in data,
      sourceSampleCount: data.source_sample_count,
      humiditySamples: data.humidity_graph?.length,
      temperatureSamples: data.temp_graph?.length,
      rootZoneSamples: data.root_zone_sample_indices?.length,
      zoneIds: data.zones == null ? undefined : Object.keys(data.zones)
    });
    let receivedSamples = false;
    if ('zones' in data) {
      for (const [key, graph] of Object.entries(data.zones)) {
        if (!(key in cur_controller.zones)) {
          continue;
        }
        if (infoGraphHasRenderableSamples(graph)) {
          set_info_graphs(cur_controller.zones[key].infos, graph, cur_controller);
          receivedSamples = true;
        }
      }
    } else if (infoGraphHasRenderableSamples(data)) {
      set_info_graphs(cur_controller.infos, data, cur_controller);
      receivedSamples = true;
    }
    if (receivedSamples) {
      cur_controller.connection.info_graphs_received = true;
      cur_controller.connection.info_graphs_received_at = Date.now();
    }
  }
  // cmd: send_info_graphs
  else if (command === 'fs_status') {
    if ('zones' in data) {
      // TODO: NOT IMPLEMENTED FOR ZONES YET!!
      for (const [key, value] of Object.entries(data.zones)) {
        cur_controller.zones[key].fs_status = value;
      }
    } else {
      cur_controller.fs_status = data;
    }
  }

  // cmd: send_recent_history:
  else if (['send_recent_history', 'send_daily_history', 'send_history'].includes(command)) {
    const target_history =
      'id' in data && cur_controller.zones?.[data.id]
        ? cur_controller.zones[data.id].history.data
        : cur_controller.history.data;
    const supportsStableTransferId = supportsStableHistoryTransferId(cur_controller);

    const is_recent = command === 'send_recent_history';
    if (is_recent) {
      console.debug(
        '[hist][recent][recv]',
        'zone=',
        'id' in data ? data.id : 'controller',
        'part=',
        data.part,
        'progress=',
        data.progress,
        'transfer=',
        data.timestamp,
        'len=',
        data.time?.length ?? 0,
        'target_progress=',
        target_history.progress ?? -1
      );
    }
    const shouldProcessHistoryChunk = shouldAcceptHistoryChunk(target_history, command, data);
    if (shouldProcessHistoryChunk) {
      markHistoryChunkReceived(target_history);
      UpdateHistory(target_history, data, { supportsStableTransferId });
      completeHistoryRequest(target_history);
    }
  }
}

function SelectHistoryValue(values) {
  // Return last value if only one or to values where gathered
  if (values.length <= 2) return values.at(-1);
  // Determine turning point
  var growing = values[1] > values[0];
  var turning_points = [];
  for (var i = 1; i < values.length; i++) {
    // If growing and the next value is not greater than the current, the current
    // value was the apex.
    if (growing && !(values[i + 1] > values[i])) turning_points.push(values[i]);
    // If falling and the next value is not smaller than the current, the current
    // value was the apex.
    if (!growing && !(values[i + 1] < values[i])) turning_points.push(values[i]);
  }
  // Otherwise always return the last value
  return turning_points.length > 0 ? turning_points.at(-1) : values.at(-1);
}

export function update_values(parent, settings, type) {
  // Convert timing and replace 0xFFFF values with -1
  const data = convert_settings(settings);
  const timestamp = data['timestamp'];
  const { cur_values: curValues = {}, multi_io_infos: multiIoInfos, ...persistentSettings } = data;

  // for industrial pro we store the multi_io_counters per zone
  if (type === 6000 && 'multi_io_counters' in data) {
    parent.multi_io_counters = reconcileState(
      parent.multi_io_counters ?? {},
      data.multi_io_counters
    );
  }

  // Current values and Multi-I/O readings are transient. Merging them into the
  // persistent settings proxy first would recursively proxy a fresh object tree
  // and then delete it again for every update.
  parent.settings = reconcilePresentCategories(parent.settings, persistentSettings);
  // `infos` combines the current-values struct with graph data received via
  // send_info_graphs. A complete cur_values snapshot may remove stale live
  // fields, but it does not own graph fields and must preserve them.
  parent.infos = reconcileStatePreservingFields(parent.infos ?? {}, curValues, infoGraphKeys);
  if (multiIoInfos !== undefined) {
    parent.multi_io_infos = reconcileState(parent.multi_io_infos ?? [], multiIoInfos);
  }

  // add to history if
  //  a) value is in history,
  //  b) history is complete and
  //  c) matches resolution
  if (
    parent.history.data.progress === 100 &&
    [resolutions.recent, 4].includes(parent.history.data.resolution)
  ) {
    const clean_timestamp = alignDownToHistoryResolution(timestamp, parent.history.data.resolution);
    const elapsed_time = clean_timestamp - parent.history.data.last_ts;
    // Add all values to later take the best of all received values.
    for (const type of HISTORY_VALUES) {
      // Get transformed value (0xFFFF=-1, co2=val, other: val/10)
      if (!(type in parent.skipped_values)) parent.skipped_values[type] = [];
      parent.skipped_values[type].push(TransformHistoryValue(parent.infos[type], type));
    }
    // Only add a values if within resolution
    if (elapsed_time > parent.history.data.resolution * 1000) {
      parent.history.data.last_ts = clean_timestamp; // Set this first
      for (const type of HISTORY_VALUES) {
        const val = SelectHistoryValue(parent.skipped_values[type]);
        parent.history.data[type].push(val);
        parent.skipped_values[type] = [val]; // remove all values but the chosen one
      }
      parent.history.data['time'].push(clean_timestamp);
    }
  }

  // update controller-date and time from current values to assure live
  // updating of said values (instead of values remaning

  // I (Felix) moved this to the TimingTab
  /*if ('time' in data['cur_values'] && parent.settings.timing) {
    const time = data['cur_values']['time'].split(':');
    parent.settings.timing.controller_time = data['cur_values']['time'];
    parent.settings.timing.time_cont_hour = parseInt(time[0]);
    parent.settings.timing.time_cont_min = parseInt(time[1]);
    parent.settings.timing.time_cont_sec = parseInt(time[2]);
  }

  if ('date' in data['cur_values'] && parent.settings.timing) {
    const date = data['cur_values']['date'].split('.');
    parent.settings.timing.controller_date = date[2] + '-' + date[1] + '-' + date[0];
    parent.settings.timing.time_cont_year = parseInt(date[2]);
    parent.settings.timing.time_cont_month = parseInt(date[1]);
    parent.settings.timing.time_cont_day = parseInt(date[0]);
  }*/

  // TODO: check if wee need this
  // handle memo names:
  if ('multi_out' in data && 'memo_names' in data['multi_out']) {
    for (const memo_name of data['multi_out']['memo_names'])
      parent.settings['multi_out'][memo_name['id']] = memo_name['name'];
  }

  if ('multi_io' in data && 'memo_names' in data['multi_io']) {
    for (const memo_name of data['multi_io']['memo_names'])
      parent.settings['multi_io'][memo_name['id']] = memo_name['name'];
  }

  // Schedule readback may arrive in separate GCSPI-backed updates; publish each
  // part into the single scheduling tab model without discarding the other.
  if (parent.settings['scheduling'] == null) {
    parent.settings['scheduling'] = {};
  }

  if ('schedule_meta' in data) {
    parent.settings['scheduling']['schedule_meta'] = normalizeScheduleMetadata(data['schedule_meta']);

  }
  if ('schedule_values' in data) {
    parent.settings['scheduling']['schedule_values'] = data['schedule_values'];
  }
  reconcileScheduleSubmission(parent, data);
}

export function set_info_graphs(infos, data, controller) {
  const hgraph = Array.isArray(data['humidity_graph']) ? data['humidity_graph'] : [];
  infos['humidity_graph'] = hgraph.map((val) => TransformHistoryValue(val, 'humidity'));
  const tgraph = Array.isArray(data['temp_graph']) ? data['temp_graph'] : [];
  infos['temp_graph'] = tgraph.map((val) => TransformHistoryValue(val, 'temp'));

  if (!supportsCharacteristicInfoGraphs(controller)) return;

  infos['info_graph_sample_interval_seconds'] = Number(data.sample_interval_seconds) || 0;
  infos['info_graph_source_sample_count'] = Number(data.source_sample_count) || 0;
  infos['climate_sample_indices'] = Array.isArray(data.climate_sample_indices)
    ? data.climate_sample_indices
    : [];
  infos['root_zone_sample_indices'] = Array.isArray(data.root_zone_sample_indices)
    ? data.root_zone_sample_indices
    : [];
  infos['vol_water_content_graphs'] = (data.vol_water_content_graphs ?? []).map((graph) =>
    (Array.isArray(graph) ? graph : []).map((value) => TransformHistoryValue(value, 'humidity'))
  );
  infos['ec_root_zone_graphs'] = (data.ec_root_zone_graphs ?? []).map((graph) =>
    (Array.isArray(graph) ? graph : []).map((value) =>
      value === 0xffff ? -1 : Number(value) / 100
    )
  );
}
